import React, { useEffect, useState } from 'react';
import { createShareLink } from '../api/share';
import { describeError } from '../lib/describeError';
import { showToast } from '../lib/toast';

// Modal that POSTs /api/v1/valuations/{id}/share and surfaces the resulting
// signed URL with a one-click copy. Auto-generates the link on mount so
// the user doesn't need an extra click.
export default function ShareDialog({ publicId, valuationName, onClose }) {
  const [state, setState] = useState({ status: 'loading' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    createShareLink(publicId)
      .then((data) => { if (!cancelled) setState({ status: 'ok', data }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', code: err?.code || 'share_failed' }); });
    return () => { cancelled = true; };
  }, [publicId]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleCopy() {
    if (state.status !== 'ok') return;
    try {
      await navigator.clipboard.writeText(state.data.share_url);
      setCopied(true);
      showToast('Share link copied.', { tone: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API can fail on insecure contexts — fall back to selection.
      const input = document.getElementById('share-url-input');
      if (input) { input.focus(); input.select(); }
    }
  }

  return (
    <>
      <div style={overlayStyle} onClick={onClose} aria-hidden="true" />
      <div style={dialogStyle} role="dialog" aria-label="Share valuation">
        <header style={headStyle}>
          <h2 style={titleStyle}>Share this valuation</h2>
          <button type="button" onClick={onClose} style={closeStyle} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div style={bodyStyle}>
          {valuationName && (
            <p style={contextStyle}><strong>{valuationName}</strong></p>
          )}
          <p style={leadStyle}>
            Anyone with this link can view a read-only summary for the next 7 days. They don't need a
            Loopora account. The link is unique and can be regenerated if you need to revoke access.
          </p>

          {state.status === 'loading' && <p style={mutedStyle}>Generating link…</p>}

          {state.status === 'error' && (
            <div style={errorBoxStyle}>
              {describeError('share', state.code)} If it keeps failing, the PDF leave-behind works as a fallback.
            </div>
          )}

          {state.status === 'ok' && (
            <>
              <label htmlFor="share-url-input" style={labelStyle}>Share link</label>
              <div style={urlRowStyle}>
                <input
                  id="share-url-input"
                  type="text"
                  readOnly
                  value={state.data.share_url}
                  onFocus={(e) => e.target.select()}
                  style={urlInputStyle}
                />
                <button type="button" onClick={handleCopy} style={copyBtnStyle}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p style={expiresStyle}>Expires {formatExpiry(state.data.expires_at)}</p>
            </>
          )}
        </div>

        <footer style={footStyle}>
          <button type="button" onClick={onClose} style={doneBtnStyle}>Done</button>
        </footer>
      </div>
    </>
  );
}

function formatExpiry(iso) {
  if (!iso) return 'in 7 days';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(31, 26, 18, 0.45)', zIndex: 50 };
const dialogStyle = {
  position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: '520px', maxWidth: '90vw',
  background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '8px',
  boxShadow: 'var(--shadow)', zIndex: 60, fontFamily: 'var(--font)',
};
const headStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 14px', borderBottom: '1px solid var(--line)' };
const titleStyle = { margin: 0, fontFamily: 'var(--display)', fontWeight: 500, fontSize: '22px', color: 'var(--ink)', letterSpacing: '-0.015em' };
const closeStyle = { width: '28px', height: '28px', borderRadius: '50%', border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink2)', display: 'grid', placeItems: 'center', cursor: 'pointer' };

const bodyStyle = { padding: '18px 22px' };
const contextStyle = { fontSize: '13.5px', color: 'var(--ink)', margin: '0 0 6px' };
const leadStyle = { fontSize: '13px', color: 'var(--ink2)', lineHeight: 1.55, margin: '0 0 18px' };
const mutedStyle = { fontSize: '13px', color: 'var(--muted)', margin: 0 };

const labelStyle = { display: 'block', fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' };
const urlRowStyle = { display: 'flex', gap: '8px' };
const urlInputStyle = {
  flex: 1, height: '40px', padding: '0 12px',
  border: '1px solid var(--line)', borderRadius: '4px',
  background: 'var(--surface)', fontSize: '12.5px', fontFamily: 'var(--mono)', color: 'var(--ink)',
};
const copyBtnStyle = {
  height: '40px', padding: '0 16px', borderRadius: '999px',
  border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff',
  fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)',
};
const expiresStyle = { fontSize: '12px', color: 'var(--muted)', marginTop: '10px' };
const errorBoxStyle = { padding: '12px', background: 'var(--warm-bg)', color: 'var(--warm)', borderRadius: '4px', fontSize: '13px', lineHeight: 1.5 };

const footStyle = { padding: '14px 22px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end' };
const doneBtnStyle = {
  height: '40px', padding: '0 18px', borderRadius: '999px',
  border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink2)',
  fontSize: '13.5px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)',
};
